import React, { useCallback, useEffect, useId, useMemo, useState } from 'react'
import styled from '@emotion/styled'
import { DEFAULT_THEME_COLORS } from '../../../../css/theme'
import { FieldErrors, FormProvider, useForm } from 'react-hook-form'

import useFlow from 'modules/flow/state/use-flow'
import FlowOneColumnStepLayout from '../one-column-step-layout/one-column-step-layout'
import useOrder from '../../../order/state/order/use-order'
import OrderDedicationHonoreeFields from '../../../order/components/dedication/order-dedication-honoree-fields'
import { DedicationFormData } from '../../../order/components/dedication/order-dedication-drawer-panel'
import { yupResolver } from '@hookform/resolvers/yup'
import useConfig from '../../../config/state/use-config'
import { Checkbox } from '../../../ui/atoms/Checkbox'
import Row from '../../../ui/atoms/Layout/Row'
import OrderDedicationRecipientFields from '../../../order/components/dedication/order-dedication-recipient-fields'
import EcardSelector from '../../../order/components/designation/EcardSelector'
import { ecard } from '../../../../models/StudioCampaign'
import OrderDedicationAddressFields from '../../../order/components/dedication/order-dedication-address-fields'
import Label from '../../../ui/atoms/Typography/Label'
import { ToggleSwitch } from '../../../ui/atoms/ToggleSwitch'
import * as Yup from 'yup'
import { EMAIL_VALIDATION_REGEX } from '../../../../utils/constants/misc'
import {
  trackFormErrorEvent,
  trackFormSubmitEvent,
} from '../../../analytics/helpers/track-form-events'

import { useTranslation } from 'next-i18next'

interface Basic {
  honoreeFirstName: string | undefined
  honoreeLastName: string | undefined
}

interface Email {
  recipientFirstName: string | undefined
  recipientLastName: string | undefined
  recipientEmailAddress: string | undefined
}

interface Ecard {
  ecardId: number | undefined
}

interface PrintedCard {
  recipientCountry: string | undefined
  recipientAddress: string | undefined
  recipientCity: string | undefined
  recipientState: string | undefined
  recipientPostalCode: string | undefined
}

type SectionValidationSchema = Yup.ObjectSchema<Basic | Email | Ecard | PrintedCard>

const DedicationHonoreeStep = () => {
  const { t } = useTranslation()
  const config = useConfig()
  const flow = useFlow()
  const order = useOrder()

  const primaryColor =
    config.theme['--theme-color__brand-primary'] ||
    DEFAULT_THEME_COLORS['--theme-color--neutral-10']

  const basicObj = useMemo(
    () => ({
      honoreeFirstName: Yup.string().required(
        t('dedicationHonoreeStep.validation.firstNameRequired', 'First name is required'),
      ),
      honoreeLastName: Yup.string().required(
        t('dedicationHonoreeStep.validation.lastNameRequired', 'Last name is required'),
      ),
    }),
    [t],
  )

  const emailObj = useMemo(
    () => ({
      recipientFirstName: Yup.string().required(
        t('dedicationHonoreeStep.validation.firstNameRequired', 'First name is required'),
      ),
      recipientLastName: Yup.string().required(
        t('dedicationHonoreeStep.validation.lastNameRequired', 'Last name is required'),
      ),
      recipientEmailAddress: Yup.string()
        .required(t('dedicationHonoreeStep.validation.emailRequired', 'Email is required'))
        .matches(
          EMAIL_VALIDATION_REGEX,
          t(
            'dedicationHonoreeStep.validation.emailFormat',
            'Please enter in the format: name@domain.com.',
          ),
        ),
    }),
    [t],
  )

  const printedCardObj = useMemo(
    () => ({
      recipientCountry: Yup.string().when('isPrintCard', {
        is: true,
        then: (schema) =>
          schema.required(
            t('dedicationHonoreeStep.validation.countryRequired', 'Country is required'),
          ),
      }),
      recipientAddress: Yup.string().when('isPrintCard', {
        is: true,
        then: (schema) =>
          schema.required(
            t('dedicationHonoreeStep.validation.addressRequired', 'Address is required'),
          ),
      }),
      recipientCity: Yup.string().when('isPrintCard', {
        is: true,
        then: (schema) =>
          schema.required(t('dedicationHonoreeStep.validation.cityRequired', 'City is required')),
      }),
      recipientState: Yup.string().when('isPrintCard', {
        is: true,
        then: (schema) =>
          schema.required(t('dedicationHonoreeStep.validation.stateRequired', 'State is required')),
      }),
      recipientPostalCode: Yup.string().when('isPrintCard', {
        is: true,
        then: (schema) =>
          schema
            .required(t('dedicationHonoreeStep.validation.zipRequired', 'Zip code is required'))
            .matches(
              /^[0-9]{5}(?:-[0-9]{4})?$/,
              t('dedicationHonoreeStep.validation.invalidZip', 'Invalid zip code'),
            ),
      }),
    }),
    [t],
  )

  const [sectionValidationSchema, setSectionValidationSchema] = useState<SectionValidationSchema>(
    Yup.object().shape(basicObj),
  )

  const goNext = useCallback(() => {
    const nextStep = flow.nextStep

    if (nextStep) {
      flow.goToStep(nextStep.name)
    }
  }, [flow])

  const dedication = order.dedication.value

  // Use the new dedicated fields instead of inferring from field values
  const defaultValues: DedicationFormData = {
    isEmailNotification: dedication.sendEmail,
    isPrintCard: dedication.sendPrintedCard,
    type: dedication.type || 'honor',
    honoreeFirstName: dedication.honoreeFirstName || '',
    honoreeLastName: dedication.honoreeLastName || '',
    recipientEmailAddress: dedication.recipientEmailAddress || '',
    recipientFirstName: dedication.recipientFirstName || '',
    recipientLastName: dedication.recipientLastName || '',
    ecardId: dedication.ecardId || undefined,
    ecardMessage: dedication.ecardMessage || '',
    recipientCountry: dedication.recipientCountry || 'US',
    recipientAddress: dedication ? dedication.recipientAddress : '',
    recipientCity: dedication.recipientCity || '',
    recipientState: dedication.recipientState || '',
    recipientPostalCode: dedication.recipientPostalCode || '',
  }

  const methods = useForm<DedicationFormData>({
    mode: 'onBlur',
    shouldFocusError: true,
    reValidateMode: 'onBlur',
    resolver: yupResolver(sectionValidationSchema),
    defaultValues,
  })

  const {
    handleSubmit,
    setFocus,
    watch,
    setValue,
    unregister,
    formState: { errors, isValid },
  } = methods

  // Trigger validation by field name
  const triggerValidationById = useCallback((fieldId: string): void => {
    const element = document.getElementById(fieldId)

    if (element instanceof HTMLElement) {
      element.focus() // Focus the field

      setTimeout(() => {
        element.blur() // Blur the field to trigger validation
      }, 0)
    }
  }, [])

  const onFormValid = useCallback(
    (data: DedicationFormData) => {
      const isNotification = data.isEmailNotification || data.isPrintCard

      const recipientInfo = {
        recipientFirstName: isNotification ? data.recipientFirstName : '',
        recipientLastName: isNotification ? data.recipientLastName : '',
        recipientName: isNotification ? `${data.recipientFirstName} ${data.recipientLastName}` : '',
        ecardMessage: isNotification ? data.ecardMessage : '',
        ecardId: isNotification ? data.ecardId : undefined,
        recipientEmailAddress: data.isEmailNotification ? data.recipientEmailAddress : '',
        recipientAddress: data.isPrintCard ? data.recipientAddress : '',
        recipientCity: data.isPrintCard ? data.recipientCity : '',
        recipientState: data.isPrintCard ? data.recipientState : '',
        recipientPostalCode: data.isPrintCard ? data.recipientPostalCode : '',
        recipientCountry: data.isPrintCard ? data.recipientCountry : '',
      }

      const dedicationPayload = {
        ...recipientInfo,
        type: data.type,
        honoreeFirstName: data.honoreeFirstName,
        honoreeLastName: data.honoreeLastName,
        honoreeName: `${data.honoreeFirstName} ${data.honoreeLastName}`,
        sendEmail: data.isEmailNotification,
        addEmailEcard: data.isEmailNotification && !!data.ecardId,
        sendPrintedCard: data.isPrintCard,
        hasDedication: true,
        isGiftAmountMessageIncluded: false,
      }

      order.dedication.setValue(dedicationPayload)
      order.dedication.save(dedicationPayload)

      trackFormSubmitEvent(flow.currentStep.name, {
        has_dedication: true,
      })
    },
    [flow.currentStep.name, order.dedication],
  )

  const onFormInvalid = useCallback(
    async (errors: FieldErrors) => {
      trackFormErrorEvent(flow.currentStep.name, Object.keys(errors)[0])

      setFocus(Object.keys(errors)[0] as keyof DedicationFormData)
    },
    [flow.currentStep.name, setFocus],
  )

  const ecardInfo = {
    id: dedication.ecardId || undefined,
    src: config.dedication.eCards.find((ecard) => ecard.id === dedication.ecardId)?.image_url ?? '',
  }

  const [isEmailToggleChecked, setIsEmailToggleChecked] = useState(dedication.sendEmail)

  const [isPrintCardToggleChecked, setIsPrintCardToggleChecked] = useState(
    dedication.sendPrintedCard,
  )

  const [isEcardEnabled, setIsEcardEnabled] = useState(dedication.ecardId ? true : false)
  const [selectedEcardInfo, setSelectedEcardInfo] = useState(ecardInfo ?? {})

  const handleEmailNotificationToggleChange = () => {
    if (isEmailToggleChecked) {
      unregister(
        ['recipientEmailAddress', 'recipientFirstName', 'recipientLastName', 'ecardMessage'],
        {
          keepDefaultValue: true,
          keepValue: false,
        },
      )
    }

    setValue('isEmailNotification', !isEmailToggleChecked)
    setIsEmailToggleChecked(!isEmailToggleChecked)
    isEmailToggleChecked && setIsEcardEnabled(false)
  }

  const handlePrintCardToggleChange = () => {
    if (isPrintCardToggleChecked) {
      unregister(
        [
          'recipientFirstName',
          'recipientLastName',
          'recipientAddress',
          'recipientCity',
          'recipientState',
          'recipientPostalCode',
          'recipientCountry',
        ],
        { keepDefaultValue: true, keepValue: false },
      )
    }

    setIsPrintCardToggleChecked(!isPrintCardToggleChecked)
    setValue('isPrintCard', !isPrintCardToggleChecked)
  }

  const handleCardSelect = (ecardInfo: ecard) => {
    setSelectedEcardInfo({ id: ecardInfo.id, src: ecardInfo.image_url })

    setValue('ecardId', ecardInfo?.id)
  }

  const onEcardToggeled = () => {
    setIsEcardEnabled(!isEcardEnabled)

    if (isEcardEnabled) {
      unregister(['ecardId'])
    }
  }

  const id = useId()

  const saveAndContinue = useCallback(async () => {
    await handleSubmit(onFormValid, onFormInvalid)()
    goNext()
  }, [goNext, handleSubmit, onFormInvalid, onFormValid])

  useEffect(() => {
    // trigger validation when toggles changes
    setSectionValidationSchema(Yup.object().shape(basicObj)),
      triggerValidationById('honoreeFirstName')

    isEmailToggleChecked &&
      (setSectionValidationSchema(Yup.object().shape(emailObj)),
      triggerValidationById('recipientEmailAddress'))

    isPrintCardToggleChecked &&
      (setSectionValidationSchema(Yup.object().shape(printedCardObj)),
      triggerValidationById('recipientAddress'))
  }, [
    isEmailToggleChecked,
    isPrintCardToggleChecked,
    printedCardObj,
    basicObj,
    emailObj,
    triggerValidationById,
  ])

  return (
    <FormProvider {...methods}>
      <StyledForm>
        <FlowOneColumnStepLayout.Main
          title={t('DedicateYourDonationStep.title', 'Dedicate your donation')}
          backButtonDataId="dedication-step-back-button"
        >
          <TextAreaWrapper>
            <div>
              <OrderDedicationHonoreeFields />

              <Row style={{ justifyContent: 'left' }}>
                {config.dedication.isEmailEnabled && (
                  <Checkbox
                    dataId="dedication-send-email-checkbox"
                    onChange={handleEmailNotificationToggleChange}
                    checked={isEmailToggleChecked}
                  >
                    {t('dedicationHonoreeStep.stage1.sendAnEmail', 'Send an email')}
                  </Checkbox>
                )}

                {config.dedication.isPrintedCardEnabled && (
                  <Checkbox
                    dataId="dedication-send-printed-card-checkbox"
                    onChange={handlePrintCardToggleChange}
                    checked={isPrintCardToggleChecked}
                  >
                    {t('dedicationHonoreeStep.stage1.sendAPrintedCard', 'Send a printed card')}
                  </Checkbox>
                )}
              </Row>
            </div>

            {(isEmailToggleChecked || isPrintCardToggleChecked) && (
              <StyledSection>
                <OrderDedicationRecipientFields
                  isEmailToggleChecked={isEmailToggleChecked}
                  errors={errors}
                />
                {isEmailToggleChecked && config.dedication.isECardEnabled && (
                  <StyledRow>
                    <ToggleSwitch
                      ariaLabelledBy={id}
                      checked={isEcardEnabled}
                      onChange={onEcardToggeled}
                      dataId="dedication-include-ecard-checkbox"
                    />
                    <Label id={id}>
                      {t('dedicationHonoreeStep.stage2.includeEcard', 'Include an eCard')}
                    </Label>
                  </StyledRow>
                )}
              </StyledSection>
            )}
            {isEcardEnabled && (
              <StyledSection>
                <EcardSelector
                  ecardId={selectedEcardInfo?.id}
                  label={t('dedicationHonoreeStep.stage3.includeEcardLabel', 'Include an Ecard')}
                  cardList={config.dedication.eCards}
                  radioGroupName="ecardId"
                  onCardSelect={handleCardSelect}
                  onEcardToggled={onEcardToggeled}
                />
              </StyledSection>
            )}
            {isPrintCardToggleChecked && (
              <StyledSection>
                <OrderDedicationAddressFields errors={errors} watch={watch} />
              </StyledSection>
            )}
          </TextAreaWrapper>
        </FlowOneColumnStepLayout.Main>

        <FlowOneColumnStepLayout.Footer>
          <FlowOneColumnStepLayout.FooterButton
            color={DEFAULT_THEME_COLORS['--theme-color--neutral-100']}
            borderColor={DEFAULT_THEME_COLORS['--theme-color--neutral-80']}
            onClick={(e) => {
              e && e.preventDefault()
              goNext()
            }}
            dataId="dedication-step-left-button"
          >
            Skip
          </FlowOneColumnStepLayout.FooterButton>
          <FlowOneColumnStepLayout.FooterButton
            disabled={!isValid}
            color={primaryColor}
            dataId="dedication-step-right-button"
            onClick={(e) => {
              e && e.preventDefault()
              saveAndContinue()
            }}
          >
            Save
          </FlowOneColumnStepLayout.FooterButton>
        </FlowOneColumnStepLayout.Footer>
      </StyledForm>
    </FormProvider>
  )
}

export default DedicationHonoreeStep

const TextAreaWrapper = styled.div`
  width: 500px;
  textarea {
    height: 78px;
  }
  @media (max-width: 767px) {
    max-width: 345px;
  }
`

const StyledSection = styled.div`
  margin-top: 20px;
`

const StyledRow = styled(Row)`
  height: 44px;
  margin-bottom: 20px;
  gap: 10px;
`

const StyledForm = styled.form`
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
`
